import {
  styled,
  spacing,
  Text,
  mq,
  dangerous_mqMax,
  css,
} from '@thg-commerce/gravity-theme'
import { Grid, GridItem } from '@thg-commerce/gravity-system'
import { Button, SafeHtml } from '@thg-commerce/gravity-elements'

import { ContentMastheadType } from './types'

interface ElementProps {
  headerType: ContentMastheadType
  hasImage: boolean
  alignTextLeft?: boolean
}

const isType = (
  type: ContentMastheadType,
  types: ContentMastheadType | ContentMastheadType[],
) => {
  const typesToCheck = typeof types === 'string' ? [types] : types
  return typesToCheck.indexOf(type) > -1
}

const highlightColour = (props) =>
  props?.hasImage
    ? props.theme.colors.palette.greys.white
    : props.theme.colors.palette.greys.darker

export const StyledGrid = styled(Grid)<ElementProps>`
  margin: 0;
  padding-left: ${spacing(2)};
  padding-right: ${spacing(2)};

  ${(props) =>
    props.headerType === ContentMastheadType.HERO &&
    `
    height: 320px;

    ${mq(props.theme.breakpointUtils.map, 'sm')} {
      height: 500px;
    }
  `}

  ${(props) =>
    props.headerType === ContentMastheadType.INSET &&
    `
    height: 296px;

    ${mq(props.theme.breakpointUtils.map, 'sm')} {
      height: 362px;
    }

    ${mq(props.theme.breakpointUtils.map, 'lg')} {
      height: 500px;
    }
  `}


  ${(props) =>
    props.headerType === ContentMastheadType.USER &&
    `
        margin: 0 auto ${spacing(2)};
        max-width: ${props.theme.site.siteWidth};
      `}
`

export const StyledPicture = styled.picture<ElementProps>`
  display: flex;
  width: 100%;
  height: 100%;

  &:after {
    content: '';
    display: block;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.25),
      rgba(0, 0, 0, 0.75) 99%
    );

    ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
      ${(props) =>
        props.headerType !== ContentMastheadType.HERO &&
        `content: unset;
        )`}
    }

    ${(props) =>
      props.headerType === ContentMastheadType.USER &&
      `background-image: none;
        `}
  }
`

export const StyledImage = styled.img`
  height: auto;
  width: 100%;
  object-fit: cover;
`

export const CategoryLink = styled(Button)<ElementProps>`
  && {
    display: inline-block;
    margin-bottom: calc(${spacing(2)} - 1px);

    ${(props) =>
      props.headerType === ContentMastheadType.HERO &&
      css`
        ${Text('bodyText', 'alternate')}
        color: ${highlightColour(props)};

        &:focus {
          color: ${highlightColour(props)};
          border-color: ${highlightColour(props)};
        }
      `}

    ${(props) =>
      props.headerType === ContentMastheadType.INSET &&
      css`
        ${Text('small', 'alternate')}
        margin-left: -2px;

        ${props.hasImage
          ? css`
              ${dangerous_mqMax(props.theme.breakpointUtils.map, 'lg')} {
                color: ${highlightColour(props)};

                &:focus {
                  color: ${highlightColour(props)};
                  border-color: ${highlightColour(props)};
                }
              }
            `
          : css`
              color: ${highlightColour(props)};

              &:focus {
                color: ${highlightColour(props)};
                border-color: ${highlightColour(props)};
              }
            `}
      `}
  }
`

export const ArticleTitle = styled.h1<ElementProps>`
  ${Text('xlarge1', 'alternate')};
  width: 100%;

  ${(props) =>
    props.headerType === ContentMastheadType.HERO &&
    `
      color: ${highlightColour(props)};
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.INSET &&
    `
      ${dangerous_mqMax(props.theme.breakpointUtils.map, 'lg')} {
        color: ${highlightColour(props)};
      }
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.USER &&
    ` margin-top: ${spacing(4)};
      text-align: center;
    `}
`

export const Description = styled.h2<ElementProps>`
  ${Text('large1', 'alternate')};

  ${(props) =>
    props.headerType === ContentMastheadType.HERO &&
    `
      color: ${highlightColour(props)};
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.INSET &&
    `
      ${dangerous_mqMax(props.theme.breakpointUtils.map, 'lg')} {
        color: ${highlightColour(props)};
      }
    `}
`

const BaseGridItem = css`
  margin-bottom: 0;
  position: relative;
`

export const TextGridItem = styled(GridItem)<ElementProps>`
  ${BaseGridItem}
  z-index: 1;

  ${(props) =>
    !props.alignTextLeft &&
    isType(props.headerType, [
      ContentMastheadType.HERO,
      ContentMastheadType.TEXTONLY,
    ]) &&
    `
        text-align: center;
        align-self: center;
        -ms-grid-row-align: center;
      `}

  ${(props) =>
    isType(props.headerType, [
      ContentMastheadType.HERO,
      ContentMastheadType.INSET,
    ]) &&
    `
      padding-top: ${spacing(3)};
      padding-bottom: ${spacing(3)};

      ${mq(props.theme.breakpointUtils.map, 'sm')} {
        margin-left: ${spacing(2)};
        margin-right: ${spacing(2)};
      }
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.INSET &&
    `
      text-align: left;
      align-self: end;
      -ms-grid-row-align: end;
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.INSET &&
    props.hasImage &&
    `
      ${mq(props.theme.breakpointUtils.map, 'lg')} {
        margin-right: ${spacing(4)};
        align-self: center;
        -ms-grid-row-align: center;
      }
    `}

  ${(props) =>
    props.headerType === ContentMastheadType.TEXTONLY &&
    `
    padding-top: ${spacing(4)};
    padding-bottom: ${spacing(4)};
    margin-left: ${spacing(4)};
    margin-right: ${spacing(4)};

    ${mq(props.theme.breakpointUtils.map, 'sm')} {
      margin-left: 0;
      margin-right: 0;
    }
  `}
`

export const ImageGridItem = styled(GridItem)`
  ${BaseGridItem}
  margin: 0 -${spacing(2)};
  height: inherit;
  z-index: 0;
`

export const Container = styled.div`
  max-width: ${(props) => props.theme.site.siteWidth};
  margin: 0 auto;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
`

export const StyledAmpContainer = styled.div<ElementProps>`
  display: flex;
  width: 100%;
  height: 100%;

  &:after {
    content: '';
    display: block;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.25),
      rgba(0, 0, 0, 0.75) 99%
    );

    ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
      ${(props) =>
        props.headerType !== ContentMastheadType.HERO &&
        `content: unset;
        )`}
    }
  }

  img {
    object-fit: cover;
  }
`
export const ArticleSubHeading = styled.h3<ElementProps>`
  ${Text('large1', 'default')};
  width: 100%;

  ${(props) =>
    props.headerType === ContentMastheadType.USER &&
    `
      text-align: center;
    `}
`

export const UserBio = styled(SafeHtml)`
  align-items: center;

  p {
    margin: ${spacing(2)} 0;
  }
`

export const UserImage = styled.img`
  width: 150px;
  height: 150px;
  margin: ${spacing(2)} 0;
  border-radius: 100%;
  object-fit: cover;
`
